/**
 * PDF preview pane — DECOUPLED from the editor's edit loop (Phase 8.3).
 *
 * Pre-Phase-8 behaviour: every keystroke debounce-fired a /preview round
 * trip (~1–3 s PDF render) and froze the right pane while it ran. The
 * right pane no longer needs to keep up with every edit, so it only
 * renders the PDF in three cases:
 *   1) On mount / when the user picks a different template or sample.
 *   2) When the user clicks the "Refresh PDF preview" button in the
 *      pane's toolbar.
 *   3) After a successful Save (ReportTemplatesPage bumps
 *      `pdfRefreshRequest` in the store post-publish).
 *
 * Edits in between mark the pane "stale" with a chip; the user clicks
 * Refresh when they want a byte-truth PDF check. Debounced auto-refresh
 * is gone.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, MenuItem, Select, IconButton, Typography, Stack, Skeleton, Alert, Chip, Tooltip } from '@mui/material';
import { ZoomIn, ZoomOut, NavigateBefore, NavigateNext, Download, Refresh as RefreshIcon } from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { reportTemplateService, type PreviewSampleRow, formatSampleOption } from '../../../services/reportTemplateService';
import { useReportTemplateStore } from './useReportTemplateStore';
import { regionsForTest, pageMarginStrips, type PdfRegion } from './sectionRegions';

// Vite + pdfjs: load the worker from the package as a URL. This is the
// recommended pattern for react-pdf v8+ with Vite.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface Props {
  templateKey: string;
  /** Used to pick which section→region map applies. */
  testName?: string | null;
}

export const PreviewPane = ({ templateKey, testName }: Props) => {
  const draftConfig = useReportTemplateStore((s) => s.draftConfig);
  const previewWodKey = useReportTemplateStore((s) => s.previewWorkOrderDetailKey);
  const setPreviewWodKey = useReportTemplateStore((s) => s.setPreviewWorkOrderDetailKey);
  const activeSection = useReportTemplateStore((s) => s.activeSection);
  // Blob URL + loading + error live in the store so they survive
  // PreviewPane re-mounts. The store setter owns URL.revokeObjectURL —
  // we never call it here.
  const pdfBlobUrl = useReportTemplateStore((s) => s.previewPdfBlobUrl);
  const setPdfBlobUrl = useReportTemplateStore((s) => s.setPreviewPdfBlobUrl);
  const loading = useReportTemplateStore((s) => s.previewPdfLoading);
  const setLoading = useReportTemplateStore((s) => s.setPreviewPdfLoading);
  const error = useReportTemplateStore((s) => s.previewPdfError);
  const setError = useReportTemplateStore((s) => s.setPreviewPdfError);
  // Phase 8.3: explicit refresh trigger. The store counter increments on
  // (a) the user clicking "Refresh PDF preview" and (b) a successful save
  // in ReportTemplatesPage. We re-fetch when this changes.
  const pdfRefreshRequest = useReportTemplateStore((s) => s.pdfRefreshRequest);
  const bumpPdfRefreshRequest = useReportTemplateStore((s) => s.bumpPdfRefreshRequest);

  const [samples, setSamples] = useState<PreviewSampleRow[]>([]);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [zoom, setZoom] = useState(1.0);
  // Phase 8.3: track whether the rendered PDF reflects the current draft.
  // Updated on every draftConfig tick (cheap — just a JSON.stringify equality
  // check). The toolbar shows a "stale" chip when true so the user knows
  // the PDF is out of date relative to their in-flight edits.
  const [renderedConfigJson, setRenderedConfigJson] = useState<string | null>(null);
  const draftConfigJson = useMemo(() => JSON.stringify(draftConfig ?? null), [draftConfig]);
  const isStale = renderedConfigJson !== null && renderedConfigJson !== draftConfigJson;

  // ── Load curated samples on mount / template change ────────────────────
  // CRITICAL: when the user switches to a different template, the old
  // `previewWodKey` is almost always stale — it points at a WOD that
  // belongs to the previous template's test type. Letting it survive
  // means the preview fires a request that the server can't fulfil,
  // surfacing as a confusing 400 in the preview pane. Reset to null
  // first, then pick the first row of the new sample list.
  useEffect(() => {
    let cancelled = false;
    setSamples([]);
    setPreviewWodKey(null);
    setPdfBlobUrl(null);
    reportTemplateService.listSamples(templateKey)
      .then((rows) => {
        if (cancelled) return;
        setSamples(rows);
        if (rows.length > 0) setPreviewWodKey(rows[0].WorkOrderDetailKey);
      })
      .catch((err) => { if (!cancelled) setError(err?.message ?? String(err)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey, setPdfBlobUrl, setError]);

  // ── Render PDF on explicit triggers only (Phase 8.3) ───────────────────
  // Triggers, in increasing user-intent order:
  //   1) Sample changed (user picked a different WOD).
  //   2) `pdfRefreshRequest` ticked (user clicked Refresh, or a successful
  //      save bumped it).
  //   3) Initial template load — covered by the sample-change effect since
  //      the sample dropdown picks a default WOD on template change.
  // Edit-the-config does NOT trigger a render anymore; the user clicks
  // "Refresh PDF" (or saves) to see edits reflected in the preview.
  const refreshKey = `${previewWodKey ?? ''}:${pdfRefreshRequest}`;
  const lastRefreshKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!draftConfig || !previewWodKey) return;
    // Skip if this is the same trigger we just rendered (e.g. parent
    // re-rendered without changing the trigger).
    if (lastRefreshKeyRef.current === refreshKey) return;
    lastRefreshKeyRef.current = refreshKey;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const blob = await reportTemplateService.renderPreview(templateKey, {
          config: draftConfig,
          workOrderDetailKey: previewWodKey,
        });
        if (cancelled) return;
        setPdfBlobUrl(URL.createObjectURL(blob));
        setRenderedConfigJson(JSON.stringify(draftConfig));
      } catch (err: unknown) {
        if (cancelled) return;
        // Axios was configured with `responseType: 'blob'`, so the
        // response body of a 400 is a Blob. Read the blob as text to
        // extract the actual Zod error JSON the server returned.
        const e = err as {
          response?: { data?: Blob | { error?: string; issues?: unknown[] } };
          message?: string;
        };
        const dataField = e?.response?.data;
        if (dataField instanceof Blob) {
          try {
            const text = await dataField.text();
            const parsed = JSON.parse(text) as { error?: string; issues?: Array<{ path?: (string|number)[]; message?: string }> };
            const issuesText = (parsed.issues ?? [])
              .slice(0, 5)
              .map((i) => `${(i.path ?? []).join('.') || '(root)'} — ${i.message ?? ''}`)
              .join('\n');
            const more = (parsed.issues?.length ?? 0) > 5 ? `\n…and ${(parsed.issues!.length - 5)} more` : '';
            setError(
              `${parsed.error ?? 'Request failed'}${issuesText ? `:\n${issuesText}${more}` : ''}`,
            );
          } catch {
            setError(e?.message ?? String(err));
          }
        } else {
          const detail = (dataField as { error?: string })?.error ?? e?.message ?? String(err);
          setError(detail);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey, templateKey, previewWodKey, draftConfig, setLoading, setError, setPdfBlobUrl]);

  // Cleanup the active blob URL on unmount. The store setter handles
  // revoke when a new URL replaces the old; here we just nudge it to
  // `null` so the final URL is freed when this pane unmounts.
  useEffect(() => () => {
    setPdfBlobUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setPageIndex((i) => Math.min(i, n - 1));
  };

  const downloadPdf = () => {
    if (!pdfBlobUrl) return;
    const a = document.createElement('a');
    a.href = pdfBlobUrl;
    a.download = `report-template-preview-${Date.now()}.pdf`;
    a.click();
  };

  const sampleOptions = useMemo(() => samples.map((s) => ({
    value: s.WorkOrderDetailKey,
    label: formatSampleOption(s, samples),
  })), [samples]);

  // True when the active TEMPLATE is the cross-template Shared Header &
  // Footer entry. The preview renders the WOD's natural test page (Atterberg
  // / PSD) with the user's draft HF overlaid, so we surface a small chip
  // making it obvious which test the user is currently looking at.
  const isSharedHeaderFooter = testName === 'Shared Header & Footer';
  const selectedSample = useMemo(
    () => samples.find((s) => s.WorkOrderDetailKey === previewWodKey),
    [samples, previewWodKey],
  );
  const previewingTestName = selectedSample?.TestName ?? null;

  // Compute the highlight region(s) for the active section in PDF points.
  // Re-derived on every config edit so margins / sizes stay in sync.
  // Page-aware: page 0 uses the standard region map, page 1 (organic
  // retest) uses the retest-specific map (only `trialTables`,
  // `resultsBlock`, and `page` margins are meaningful there).
  const highlights = useMemo<PdfRegion[]>(() => {
    if (!activeSection) return [];
    const map = regionsForTest(testName, draftConfig, pageIndex);
    if (!map) return [];
    const region = map[activeSection];
    if (!region || region.w === 0 || region.h === 0) return [];
    if (region.style === 'frame') return pageMarginStrips(draftConfig);
    return [region];
  }, [activeSection, draftConfig, testName, pageIndex]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', minWidth: 56 }}>
          {isSharedHeaderFooter ? 'Render on:' : 'Sample:'}
        </Typography>
        <Select
          size="small"
          value={previewWodKey ?? ''}
          onChange={(e) => setPreviewWodKey(e.target.value || null)}
          sx={{ minWidth: 220, fontSize: '0.85rem' }}
          displayEmpty
        >
          <MenuItem value="" disabled>Choose a sample…</MenuItem>
          {sampleOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
        {/* Cross-template Shared Header & Footer: show what test page the
            preview is rendering against, since header/footer changes apply
            to every report and the preview picks ONE to draw against. */}
        {isSharedHeaderFooter && previewingTestName && (
          <Tooltip title="Header & footer changes apply to every report. The preview shows them overlaid on this test's page so you can see them in context. Pick a different sample above to preview on a different test type.">
            <Chip
              size="small"
              label={`On ${previewingTestName}`}
              color="info"
              variant="outlined"
              sx={{ fontSize: '0.72rem' }}
            />
          </Tooltip>
        )}
        {isStale && (
          <Tooltip title="The PDF preview was rendered from a previous draft. Click Refresh to render the current draft.">
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="stale"
              sx={{ height: 18, fontSize: 10 }}
            />
          </Tooltip>
        )}
        <Tooltip title="Render the current draft to PDF. This is a real PDF generation (~1 s); the live editor in the centre pane updates instantly without it.">
          <span>
            <Button
              size="small"
              variant={isStale ? 'contained' : 'outlined'}
              color={isStale ? 'warning' : 'primary'}
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={bumpPdfRefreshRequest}
              disabled={loading || !draftConfig || !previewWodKey}
              sx={{ ml: 1 }}
            >
              {loading ? 'Rendering…' : 'Refresh PDF'}
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))}><ZoomOut fontSize="small" /></IconButton>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
        <IconButton size="small" onClick={() => setZoom((z) => Math.min(2.5, z + 0.1))}><ZoomIn fontSize="small" /></IconButton>
        {numPages > 1 && (
          <>
            <IconButton size="small" disabled={pageIndex === 0} onClick={() => setPageIndex((i) => Math.max(0, i - 1))}>
              <NavigateBefore fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 60, textAlign: 'center' }}>
              {pageIndex + 1} / {numPages}
            </Typography>
            <IconButton size="small" disabled={pageIndex >= numPages - 1} onClick={() => setPageIndex((i) => Math.min(numPages - 1, i + 1))}>
              <NavigateNext fontSize="small" />
            </IconButton>
          </>
        )}
        <IconButton size="small" disabled={!pdfBlobUrl} onClick={downloadPdf}><Download fontSize="small" /></IconButton>
      </Stack>

      <Box sx={{
        flex: 1, overflow: 'auto', display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', p: 2, bgcolor: 'grey.100',
      }}>
        {error && (
          <Alert severity="error" sx={{
            width: '100%',
            // Preserve newlines so multi-line Zod issue listings read.
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: 11,
            '& .MuiAlert-message': { width: '100%' },
          }}>{error}</Alert>
        )}
        {!error && loading && (
          <Skeleton variant="rectangular" sx={{ width: 612 * zoom, height: 792 * zoom }} />
        )}
        {!error && !loading && pdfBlobUrl && (
          // Relative wrapper so the highlight overlay can be absolutely
          // positioned on top of the rendered Page using the same PDF-pt
          // → pixel mapping as the Page itself (px = pt × zoom).
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Document
              file={pdfBlobUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(err) => setError(err?.message ?? 'PDF load error')}
              loading={<Skeleton variant="rectangular" sx={{ width: 612 * zoom, height: 792 * zoom }} />}
            >
              <Page
                pageIndex={pageIndex}
                scale={zoom}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </Document>
            {/* Section highlight overlay — translucent rectangle(s)
                over the rendered page that fade in/out as the user
                expands different form sections. pointer-events: none
                so it never intercepts clicks. */}
            {highlights.map((r, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left:   r.x * zoom,
                  top:    r.y * zoom,
                  width:  r.w * zoom,
                  height: r.h * zoom,
                  pointerEvents: 'none',
                  border: '2px solid',
                  borderColor: 'warning.main',
                  // Soft amber wash + outer glow so the highlight reads even
                  // on busy chart pages without obscuring detail underneath.
                  backgroundColor: 'rgba(237, 108, 2, 0.10)',
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.0)',
                  borderRadius: 0.5,
                  transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            ))}
          </Box>
        )}
        {!error && !loading && !pdfBlobUrl && (
          <Typography color="text.secondary" sx={{ mt: 4, fontStyle: 'italic' }}>
            Click "Refresh PDF" to render the current draft.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
